import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '@/hooks/use-auth';
import {
  getNotificationsService,
  markAllNotificationsReadService,
  markNotificationReadService,
  type IPlatformNotification,
} from '@/services/notifications';

const POLL_INTERVAL_MS = 30000;

const formatRelative = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

export const NotificationCenter: React.FC = () => {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const [items, setItems] = useState<IPlatformNotification[]>([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    if (!accessToken) return;
    try {
      const { items: fetched, unreadCount } =
        await getNotificationsService(accessToken);
      setItems(Array.isArray(fetched) ? fetched : []);
      setUnread(unreadCount);
    } catch {
      // silent — keep last known state
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;
    load();
    const timer = window.setInterval(load, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [accessToken, load]);

  const handleMarkRead = async (id: string) => {
    if (!accessToken) return;
    setItems((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    setUnread((u) => Math.max(0, u - 1));
    try {
      await markNotificationReadService(id, accessToken);
    } catch {
      load();
    }
  };

  const handleMarkAll = async () => {
    if (!accessToken) return;
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try {
      await markAllNotificationsReadService(accessToken);
    } catch {
      load();
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative flex items-center justify-center transition-colors rounded-lg w-9 h-9 hover:bg-slate-100"
          aria-label={t('notifications.ariaLabel')}
        >
          <Bell className="w-5 h-5 text-slate-700" />
          {unread > 0 && (
            <Badge className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px]">
              {unread > 9 ? '9+' : unread}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold">{t('notifications.title')}</span>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-0 text-xs text-primary"
              onClick={handleMarkAll}
            >
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-sm text-center text-muted-foreground">
              {t('notifications.empty')}
            </p>
          ) : (
            <ul className="divide-y">
              {items.map((n) => (
                <li
                  key={n.id}
                  className={`flex gap-3 px-4 py-3 ${n.read ? '' : 'bg-primary/5'}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{n.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {n.message}
                    </p>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      {formatRelative(n.createdAt)}
                    </p>
                  </div>
                  {!n.read && (
                    <button
                      type="button"
                      onClick={() => handleMarkRead(n.id)}
                      className="self-start text-muted-foreground hover:text-primary"
                      aria-label={t('notifications.markRead')}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
